import { useState } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { API_URL, NETWORK_DOWN_MESSAGE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, ChevronsUpDown } from "lucide-react";
import { rankCountries } from "@/lib/country-search";

interface Country {
  code: string;
  name: string;
}

const requestAccessSchema = z.object({
  name: z.string().trim().min(1, "Enter your name"),
  email: z.email("Enter a valid email address"),
  firm_name: z.string().trim(),
  country: z.string().min(1, "Select your country"),
});

type RequestAccessForm = z.infer<typeof requestAccessSchema>;

export function RequestAccess() {
  const [submittedEmail, setSubmittedEmail] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    control,
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<RequestAccessForm>({
    resolver: zodResolver(requestAccessSchema),
    defaultValues: { name: "", email: "", firm_name: "", country: "" },
  });

  const {
    data: countryList,
    isError: isCountriesError,
    refetch: refetchCountries,
  } = useQuery({
    queryKey: ["countries"],
    queryFn: async (): Promise<Country[]> => {
      const res = await fetch(`${API_URL}/api/countries`);
      if (!res.ok) throw new Error("Failed to load countries");
      return res.json();
    },
    staleTime: Infinity,
  });

  const onSubmit = async (values: RequestAccessForm) => {
    setSubmitError(null);
    try {
      const res = await fetch(`${API_URL}/api/accountant/auth/request-access`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(values),
      });
      if (res.ok) {
        setSubmittedEmail(values.email);
      } else if (res.status === 429) {
        setSubmitError(
          "Too many requests from your network. Please try again in an hour."
        );
      } else if (res.status >= 502 && res.status <= 504) {
        setSubmitError(NETWORK_DOWN_MESSAGE);
      } else {
        setSubmitError("Something went wrong. Please try again.");
      }
    } catch {
      setSubmitError(NETWORK_DOWN_MESSAGE);
    }
  };

  // Success state — request recorded, access is granted manually
  if (submittedEmail) {
    return (
      <div className="flex min-h-svh items-center justify-center bg-white px-4">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-highlight-muted text-highlight">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-8 w-8"
              aria-hidden="true"
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>

          <div className="space-y-2">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground">
              Request received
            </h1>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Our team sets up each portal by hand. Once your access is
              approved, we'll email{" "}
              <strong className="text-foreground font-medium">
                {submittedEmail}
              </strong>{" "}
              a secure login link. No password needed.
              <br />
              <br />
              Questions in the meantime? Email{" "}
              <a
                href="mailto:support@taxr.io"
                className="text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
              >
                support@taxr.io
              </a>
              .
            </p>
          </div>

          <Link
            to="/login"
            className="inline-block text-sm text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-svh flex-col items-center justify-center px-6 py-12 bg-white">
      <div className="w-full max-w-[400px] space-y-8">
        {/* Logo */}
        <div className="flex items-center gap-2.5">
          <img src="/taxr-logo.png" alt="Taxr logo" className="h-7 w-auto" />
          <span className="font-semibold text-base tracking-tight text-foreground">
            Taxr
          </span>
        </div>

        {/* Header */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-widest text-highlight">
            Accountant Portal
          </p>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            Request access
          </h1>
          <p className="text-sm text-muted-foreground">
            The Taxr portal is free for accountants and bookkeepers. Tell us
            about your practice and we'll set you up.
          </p>
        </div>

        {/* Submit error */}
        {submitError && (
          <div
            role="alert"
            className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"
          >
            {submitError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5" noValidate>
          <div className="space-y-2">
            <Label htmlFor="ra-name" className="text-sm font-medium">
              Full name
            </Label>
            <Input
              id="ra-name"
              autoComplete="name"
              placeholder="Jane Doe"
              className="h-10 text-sm"
              aria-invalid={!!errors.name}
              {...register("name")}
            />
            {errors.name && (
              <p className="text-xs text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra-email" className="text-sm font-medium">
              Work email
            </Label>
            <Input
              id="ra-email"
              type="email"
              autoComplete="email"
              placeholder="jane@yourfirm.com"
              className="h-10 text-sm"
              aria-invalid={!!errors.email}
              {...register("email")}
            />
            {errors.email && (
              <p className="text-xs text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra-firm" className="text-sm font-medium">
              Firm or practice name{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Input
              id="ra-firm"
              autoComplete="organization"
              placeholder="Doe & Co Accounting"
              className="h-10 text-sm"
              {...register("firm_name")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="ra-country" className="text-sm font-medium">
              Country
            </Label>
            {isCountriesError ? (
              <p className="text-xs text-muted-foreground">
                Couldn't load the country list.{" "}
                <button
                  type="button"
                  onClick={() => refetchCountries()}
                  className="text-foreground underline underline-offset-4"
                >
                  Try again
                </button>
              </p>
            ) : (
              <Controller
                control={control}
                name="country"
                render={({ field }) => (
                  <CountryCombobox
                    countries={countryList ?? []}
                    value={field.value}
                    onChange={field.onChange}
                    invalid={!!errors.country}
                  />
                )}
              />
            )}
            {errors.country && (
              <p className="text-xs text-destructive">
                {errors.country.message}
              </p>
            )}
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? "Sending request..." : "Request access"}
          </Button>

          <p className="text-center text-xs text-muted-foreground leading-relaxed">
            Access is granted manually by our team, so every portal stays a
            real accounting practice. We'll email you as soon as
            you're approved.
          </p>
        </form>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Already have access?{" "}
          <Link
            to="/login"
            className="text-foreground underline underline-offset-4 decoration-muted-foreground/40 hover:decoration-foreground transition-colors"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}

/**
 * Searchable country picker. A plain <Select> over ~250 countries means
 * scrolling to find one; typing two letters is the whole interaction here.
 *
 * Matching and ordering are done here (rankCountries), not by the command
 * list: its built-in sort only reorders rows that were already on screen for
 * the previous search text, so the best match was not reliably first.
 */
function CountryCombobox({
  countries,
  value,
  onChange,
  invalid,
}: {
  countries: { code: string; name: string }[];
  value: string;
  onChange: (value: string) => void;
  invalid: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const selected = countries.find((c) => c.code === value);
  const matches = rankCountries(countries, search);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setSearch("");
      }}
    >
      <PopoverTrigger
        id="ra-country"
        role="combobox"
        aria-expanded={open}
        aria-invalid={invalid}
        aria-label="Country"
        className="flex w-full h-10 items-center justify-between rounded-md border border-input bg-transparent px-3 text-sm"
      >
        <span className={selected ? "" : "text-muted-foreground"}>
          {selected?.name ?? "Select your country"}
        </span>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent
        className="w-(--anchor-width) p-0"
        align="start"
      >
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search countries..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No country found.</CommandEmpty>
            <CommandGroup>
              {matches.map((c) => (
                <CommandItem
                  key={c.code}
                  value={c.name}
                  onSelect={() => {
                    onChange(c.code);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <Check
                    className={
                      "mr-2 h-4 w-4 " +
                      (c.code === value ? "opacity-100" : "opacity-0")
                    }
                  />
                  {c.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
